import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Drawer, Modal } from "antd";
import { CheckCircleFilled, CopyOutlined, DownloadOutlined } from "@ant-design/icons";
import {
  isAndroid,
  isIOS,
  isIOSNonSafari,
  isStandalone,
  useInstallPWA,
} from "../../hooks/useInstallPWA";
import "./InstallAppButton.css";

/** iOS 分享鍵圖示（SF Symbol square.and.arrow.up）：認得出那顆鍵就是引導的全部重點，
 *  自己畫比拿 antd 的近似 icon 準（ShareAltOutlined 是 Android 樣式，會誤導） */
function ShareIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="1em"
      height="1em"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3.5v10.5" />
      <path d="M8 7.5l4-4 4 4" />
      <path d="M7.5 11.5H5.5a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1v-7a1 1 0 0 0-1-1h-2" />
    </svg>
  );
}

/** 分享鍵位置隨機型差很大，實機截圖比文字描述有效：
 *  有的機型網址列旁就是分享鍵，有的只給「⋯」，要再點開才看得到分享。
 *  圖檔在 public/install/，原始尺寸不一，靠 width:100% + height:auto 自適應。 */
const IOS_SHARE_SHOTS = [
  { src: "/install/ios-share-urlbar.png", captionKey: "installApp.iosShotUrlBar" },
  { src: "/install/ios-share-dots.png", captionKey: "installApp.iosShotDots" },
  { src: "/install/ios-share-dots-menu.png", captionKey: "installApp.iosShotDotsMenu" },
];

function Step({ no, children }) {
  return (
    <li>
      <span className="install-app__step-no">{no}</span>
      <span className="install-app__step-text">{children}</span>
    </li>
  );
}

/**
 * 「安裝 App」按鈕。
 *
 * - Android Chromium 系：收到 beforeinstallprompt 時點一下就跳系統原生安裝對話框，
 *   全程不需閱讀任何步驟；收不到才退回兩步手動說明（最可能是已經安裝過了）。
 * - iOS Safari：沒有安裝 API（WebKit 未實作），只能開底部 sheet 給三步圖像引導。
 * - iOS 非 Safari：第三方瀏覽器與 webview 做不出 standalone，加了主畫面也收不到推播，
 *   所以只導去 Safari，刻意不給安裝步驟。
 *
 * 行為與驗收見 docs/PRD/02-安裝引導與加入主畫面.md，技術細節見
 * docs/TECH/01-PWA-安裝與安裝引導.md。
 */
export default function InstallAppButton() {
  const { t } = useTranslation();
  const { canInstall, installed, install } = useInstallPWA();
  const [sheet, setSheet] = useState(null); // "android" | "ios" | null
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const ios = isIOS();

  // 已經從圖示開啟就沒必要提示（也必須擋在 isIOSNonSafari 之前，standalone 的 UA 會誤判）
  if (isStandalone()) return null;
  // 桌機不顯示：使用者不會想把車隊 App 裝在電腦上，Chrome 自己的網址列圖示已足夠
  if (!ios && !isAndroid()) return null;

  // iOS 的第三方瀏覽器／App 內建 webview：只導去 Safari
  if (isIOSNonSafari()) {
    const copyUrl = async () => {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopied(true);
      } catch {
        // 剪貼簿被拒也沒關係，下面一直顯示著網址可以長按複製
        setCopied(false);
      }
    };
    return (
      <section className="install-app install-app--safari">
        <p className="install-app__safari-title">{t("installApp.safariTitle")}</p>
        <p className="install-app__safari-body">{t("installApp.safariBody")}</p>
        <p className="install-app__url">{window.location.href}</p>
        <Button
          block
          size="large"
          icon={copied ? <CheckCircleFilled /> : <CopyOutlined />}
          onClick={copyUrl}
          className="install-app__btn"
        >
          {t(copied ? "installApp.safariCopied" : "installApp.safariCopy")}
        </Button>
      </section>
    );
  }

  if (installed) {
    return (
      <p className="install-app__done">
        <CheckCircleFilled /> {t("installApp.installed")}
      </p>
    );
  }

  const handleClick = async () => {
    if (ios) {
      setSheet("ios");
      return;
    }
    if (!canInstall) {
      setSheet("android");
      return;
    }
    setBusy(true);
    const outcome = await install();
    setBusy(false);
    // prompt() 拿不到或出錯才給手動說明；使用者自己按取消（dismissed）不用再囉嗦
    if (outcome === "unavailable" || outcome === "error") setSheet("android");
  };

  const closeSheet = () => setSheet(null);

  return (
    <section className="install-app">
      <Button
        type="primary"
        ghost
        block
        size="large"
        icon={<DownloadOutlined />}
        loading={busy}
        onClick={handleClick}
        className="install-app__btn"
      >
        {t("installApp.button")}
      </Button>

      {/* Android fallback：兩步手動說明 */}
      <Modal
        open={sheet === "android"}
        onCancel={closeSheet}
        centered
        title={t("installApp.manualTitle")}
        footer={
          <Button block size="large" onClick={closeSheet}>
            {t("installApp.manualClose")}
          </Button>
        }
      >
        <ol className="install-app__steps">
          <Step no="1">
            {t("installApp.manualStep1")}
            <span className="install-app__dots" aria-hidden="true">
              ⋮
            </span>
          </Step>
          <Step no="2">{t("installApp.manualStep2")}</Step>
        </ol>
        <p className="install-app__already">{t("installApp.manualAlready")}</p>
      </Modal>

      {/* iOS Safari：底部 sheet。
          步驟不寫分享鍵在哪，因為位置隨機型變化很大（下方工具列／網址列旁／iPad 在右上），
          寫死方向反而指錯；圖示與實機截圖才是跨機型都成立的定位依據。 */}
      <Drawer
        open={sheet === "ios"}
        onClose={closeSheet}
        placement="bottom"
        height="auto"
        title={t("installApp.iosTitle")}
        className="install-app__sheet"
      >
        <ol className="install-app__steps">
          <Step no="1">
            {t("installApp.iosStep1")}
            <span className="install-app__share" aria-hidden="true">
              <ShareIcon />
            </span>
          </Step>
          <li className="install-app__shots">
            {IOS_SHARE_SHOTS.map((shot) => (
              <figure key={shot.src} className="install-app__shot">
                <img src={shot.src} alt={t(shot.captionKey)} loading="lazy" />
                <figcaption>{t(shot.captionKey)}</figcaption>
              </figure>
            ))}
          </li>
          <Step no="2">{t("installApp.iosStep2")}</Step>
          <Step no="3">{t("installApp.iosStep3")}</Step>
        </ol>
      </Drawer>
    </section>
  );
}
